/**
 * @author        matthew@zumper.com (Matthew Talamini)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   groupFloorplansByBedsBaths (copied from nr)
 */

import {
  selectIsRoomType,
  selectRoomCategoryText,
} from '@zcode/nr/es/shared/bizos/rentable/selectors/floorplan'
import type { FloorplanListing, NormalFloorplanListingData } from '@zcode/state-type'
import invariant from 'tiny-invariant'

export interface FloorplanGroup {
  allUnitsWaitListed?: boolean
  bedrooms: number
  count: number
  fpId: number
  minPrice: number
  maxPrice: number
}

interface FloorplanGroupMetadata {
  fpIdIsWaitlisted: boolean
  minNonWaitlistedPrice: number
}

export const createFloorplansIdDict = (
  floorplans: ReadonlyArray<FloorplanListing<NormalFloorplanListingData>> | undefined
) => {
  const fps: {
    [listingId: number]:
      | ReturnType<typeof selectRoomCategoryText>
      | FloorplanListing<NormalFloorplanListingData>['bedsBathsTxt']
  } = {}

  if (floorplans) {
    for (const fp of floorplans) {
      const { bedsBathsTxt, listingId } = fp
      const finalBedsBathsTxt = selectIsRoomType(fp) ? selectRoomCategoryText(fp) : bedsBathsTxt
      fps[listingId] = finalBedsBathsTxt
    }
  }

  return fps
}

export const groupFloorplansByBedsBaths = (
  floorplans: ReadonlyArray<FloorplanListing<NormalFloorplanListingData>> | undefined,
  selectedFloorplan?: FloorplanListing<NormalFloorplanListingData> | undefined
) => {
  const fps = new Map<
    | ReturnType<typeof selectRoomCategoryText>
    | FloorplanListing<NormalFloorplanListingData>['bedsBathsTxt'],
    FloorplanGroup
  >()
  const selectedFloorplanBedsBathsTxt = selectIsRoomType(selectedFloorplan)
    ? selectRoomCategoryText(selectedFloorplan)
    : selectedFloorplan?.bedsBathsTxt

  if (floorplans) {
    const metadataByGroup = new WeakMap<FloorplanGroup, FloorplanGroupMetadata>()

    for (const fp of floorplans) {
      const { bedsBathsTxt, isActive, waitlistEnabled } = fp
      const floorplanText = selectIsRoomType(fp) ? selectRoomCategoryText(fp) : bedsBathsTxt

      if (isActive) {
        let group = fps.get(floorplanText)
        let metadata: FloorplanGroupMetadata | undefined
        if (!group) {
          fps.set(
            floorplanText,
            (group = {
              bedrooms: fp.bedrooms,
              count: 0,
              fpId: fp.listingId,
              minPrice: Number.POSITIVE_INFINITY,
              maxPrice: Number.NEGATIVE_INFINITY,
            })
          )
          metadata = {
            fpIdIsWaitlisted: fp.waitlistEnabled,
            minNonWaitlistedPrice: Number.POSITIVE_INFINITY,
          }
          invariant(group)
          metadataByGroup.set(group, metadata)
        }
        metadata = metadataByGroup.get(group)
        invariant(metadata)

        const minPrice = group.minPrice || Number.POSITIVE_INFINITY
        const maxPrice = group.maxPrice || Number.NEGATIVE_INFINITY
        const fpMinPrice = fp.minPrice || fp.price || 0
        const fpMaxPrice = fp.maxPrice || fp.price || 0

        group.minPrice = Math.min(minPrice, fpMinPrice)
        group.maxPrice = Math.max(maxPrice, fpMaxPrice)
        if (!fp.waitlistEnabled) {
          metadata.minNonWaitlistedPrice = Math.min(
            fpMinPrice,
            metadata.minNonWaitlistedPrice || Number.POSITIVE_INFINITY
          )
        }

        if (
          // any non-waitlisted FP is better than any waitlisted FP
          (metadata.fpIdIsWaitlisted && !fp.waitlistEnabled) ||
          // if both FPs are waitlisted, the lower-priced one is better
          (metadata.fpIdIsWaitlisted &&
            fp.waitlistEnabled &&
            (group.minPrice === fp.minPrice || fpMinPrice === 0)) ||
          // if neither FP is waitlisted, the lower-priced one is better
          (!metadata.fpIdIsWaitlisted &&
            !fp.waitlistEnabled &&
            metadata.minNonWaitlistedPrice === fp.minPrice)
        ) {
          selectedFloorplanBedsBathsTxt === floorplanText && selectedFloorplan
            ? (group.fpId = selectedFloorplan.listingId)
            : (group.fpId = fp.listingId)
          metadata.fpIdIsWaitlisted = fp.waitlistEnabled
        }

        group.count = (group.count || 0) + 1

        group.bedrooms = fp.bedrooms
        group.allUnitsWaitListed = group.allUnitsWaitListed === false ? false : waitlistEnabled
      }
    }
  }

  return fps
}

type DropdownOption = [string, FloorplanGroup]

export const sortOptions = (a: DropdownOption, b: DropdownOption) => {
  const optionA = a[1]
  const optionB = b[1]
  if (optionA?.allUnitsWaitListed !== optionB?.allUnitsWaitListed) {
    return optionA?.allUnitsWaitListed ? 1 : -1 // floorplans with all units waitlisted last
  } else {
    return 0
  }
}
